import { useCallback, useEffect, useRef, useState } from "react";
import type { PublicConfig } from "../../api/types";

const SCRIPT_ID = "facego-turnstile";

interface Turnstile {
  token: string;
  enabled: boolean;
  containerRef: (node: HTMLDivElement | null) => void;
  reset: () => void;
}

/**
 * Port từ bản vanilla: nạp script Cloudflare một lần, render widget explicit,
 * và xoá token mỗi khi hết hạn hoặc lỗi để form không gửi kèm token chết.
 */
export function useTurnstile(config: PublicConfig | undefined): Turnstile {
  const enabled = Boolean(config?.turnstile?.enabled && config.turnstile.site_key);
  const [token, setToken] = useState("");
  const widgetId = useRef<string | null>(null);
  const container = useRef<HTMLDivElement | null>(null);

  const render = useCallback(() => {
    if (!enabled || !container.current || widgetId.current || !window.turnstile) return;
    widgetId.current = window.turnstile.render(container.current, {
      sitekey: config?.turnstile.site_key,
      theme: "auto",
      callback: (value: string) => setToken(value),
      "expired-callback": () => setToken(""),
      "error-callback": () => setToken(""),
    });
  }, [config?.turnstile.site_key, enabled]);

  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      container.current = node;
      if (node) render();
    },
    [render],
  );

  useEffect(() => {
    if (!enabled) return;
    if (window.turnstile) {
      render();
      return;
    }
    window.facegoTurnstileReady = render;
    if (document.getElementById(SCRIPT_ID)) return;
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=facegoTurnstileReady&render=explicit";
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, [enabled, render]);

  const reset = useCallback(() => {
    setToken("");
    if (window.turnstile && widgetId.current) window.turnstile.reset(widgetId.current);
  }, []);

  return { token, enabled, containerRef, reset };
}
